import { Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { buttonVariants } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAvailabilityResourceMonth } from "@/hooks/use-availability";
import { findCandidates } from "@/lib/availability/candidates";
import { type AvailabilityCutoffs, isFreeState, isOverState } from "@/lib/availability/cell-state";
import { monthLabel } from "@/lib/availability/months";
import { cn } from "@/lib/utils";
import type { AvailabilityCellPayload } from "@/schemas/availability";
import { RowChip, StateChip } from "./Chips";
import { BAR_CLASS, cellText } from "./cell-style";
import { formatHours, fullMonthLabel, ISSUE_TEXT, POD_POSITION_HINT, STATE_LABEL } from "./labels";
import { barRatio, trendMonths, type ViewRow } from "./model";
import { Note, PanelLabel, PanelShell, SplitBar, Stat } from "./PanelParts";

const KIND_LABEL: Record<AvailabilityCellPayload["kind"], string> = {
  actual: "ACTUAL",
  mtd: "MONTH TO DATE",
  forecast: "FORECAST",
};

function bookedSub(cell: AvailabilityCellPayload): string {
  if (cell.kind === "actual") return "Billable actuals";
  if (cell.kind === "mtd") return "Actuals to date plus forecast";
  return cell.hasProposed
    ? `incl. ${formatHours(cell.proposedHours)} h proposed`
    : "Active Guidance";
}

interface ResourceMonthPanelProps {
  row: ViewRow;
  month: string;
  /** Everyone in the report, for "who could absorb". Non-delivery people never qualify. */
  allRows: readonly ViewRow[];
  cutoffs: AvailabilityCutoffs;
  /** The Resources page is not shown to every role; hide the link rather than dead-end. */
  canOpenResource: boolean;
  onClose: () => void;
}

export function ResourceMonthPanel({
  row,
  month,
  allRows,
  cutoffs,
  canOpenResource,
  onClose,
}: ResourceMonthPanelProps) {
  const cell = row.cells[month];
  const hasSolutions = cell !== undefined && cell.activeHours + cell.proposedHours > 0;
  const solutions = useAvailabilityResourceMonth(row.resourceId, month, hasSolutions);

  const candidates = useMemo(() => {
    if (cell === undefined || !isOverState(cell.state)) return [];
    return findCandidates({
      target: {
        resourceId: row.resourceId,
        disciplineCode: row.disciplineCode,
        homeRole: row.homeRole,
      },
      month,
      rows: allRows.filter((other) => !other.nonDelivery),
      cutoffs,
    });
  }, [cell, row, month, allRows, cutoffs]);

  if (cell === undefined) return null;

  const meta = [
    row.homeRole?.trim() || "No Home Role",
    row.disciplineCode ?? "No Discipline",
    ...(row.primaryAccount !== null ? [row.primaryAccount.name] : []),
  ].join(" · ");
  const hasHours = cell.availableHours !== null;
  const over = Math.max(-cell.netHours, 0);
  const trend = trendMonths(Object.keys(row.cells), month).flatMap((m) => {
    const trendCell = row.cells[m];
    return trendCell === undefined ? [] : [{ month: m, cell: trendCell }];
  });
  const need = Math.round(over);
  const covered = Math.round(candidates.reduce((sum, c) => sum + c.freeHours, 0));
  const scope = row.disciplineCode ?? "this Home Role";

  return (
    <PanelShell
      label="Month detail"
      eyebrow={`${fullMonthLabel(month)} · ${KIND_LABEL[cell.kind]}`}
      title={row.name}
      subtitle={meta}
      onClose={onClose}
    >
      <div className="flex flex-wrap items-center gap-2">
        <StateChip state={cell.state} />
        {row.chronic ? <RowChip kind="chronic" /> : null}
        {cell.issues.length > 0 ? (
          <span className="inline-flex h-6 items-center rounded-full border border-[color:var(--avail-issue)] px-2.5 font-mono text-xs font-medium tracking-wide text-heading uppercase">
            Issue
          </span>
        ) : null}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Stat
          label="Available Hours"
          value={hasHours ? `${formatHours(cell.availableHours ?? 0)} h` : "–"}
        />
        <Stat
          label="Booked"
          value={hasHours ? `${formatHours(cell.bookedHours)} h` : "–"}
          sub={hasHours ? bookedSub(cell) : undefined}
        />
        <Stat
          label="Net"
          value={
            !hasHours
              ? "–"
              : cell.netHours < 0
                ? `+${formatHours(over)} h`
                : `${formatHours(cell.netHours)} h free`
          }
          tone={!hasHours ? undefined : cell.netHours < 0 ? "over" : "free"}
        />
      </div>

      {hasHours ? <SplitBar netHours={cell.netHours} availableHours={cell.availableHours} /> : null}

      {cell.issues.length > 0 ? (
        <Note tone="warning">
          <strong>Check the data.</strong> {cell.issues.map((issue) => ISSUE_TEXT[issue]).join(" ")}
        </Note>
      ) : null}
      {cell.state === "unplanned" ? (
        <Note tone="neutral">No Guidance this far out. This month is unknown, not free.</Note>
      ) : null}
      {cell.state === "noData" ? (
        <Note tone="neutral">No Available Hours on record for this month.</Note>
      ) : null}

      {hasSolutions ? (
        <div className="flex flex-col">
          <PanelLabel className="pb-1.5">Solutions this month</PanelLabel>
          {solutions.isLoading ? (
            <div className="flex flex-col gap-2 border-t pt-2">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          ) : solutions.isError ? (
            <ErrorState
              message="Solutions could not be loaded."
              onRetry={() => solutions.refetch()}
            />
          ) : solutions.data === undefined || solutions.data.solutions.length === 0 ? (
            <span className="border-t py-2 text-xs text-muted-foreground">
              No Solutions this month.
            </span>
          ) : (
            solutions.data.solutions.map((solution) => (
              <div
                key={`${solution.solutionId}-${solution.status}`}
                className="flex items-center gap-2.5 border-t py-2"
              >
                <div className="flex min-w-0 flex-1 flex-col gap-px">
                  <span className="truncate text-sm font-semibold text-heading">
                    {solution.name}
                  </span>
                  <span className="truncate text-xs text-muted-foreground">
                    {solution.accountName ?? "No Account"}
                    {solution.podPosition !== null ? (
                      <>
                        {" · "}
                        <span title={POD_POSITION_HINT[solution.podPosition]}>
                          {solution.podPosition}
                        </span>
                      </>
                    ) : null}
                  </span>
                </div>
                <span
                  className={cn(
                    "shrink-0 rounded-full px-2 py-px font-mono text-xs tracking-wide uppercase",
                    solution.status === "proposed"
                      ? "border border-primary text-primary"
                      : "bg-muted text-heading",
                  )}
                >
                  {solution.status}
                </span>
                <span className="w-14 shrink-0 text-right font-mono text-sm text-heading">
                  {formatHours(solution.hours)} h
                </span>
              </div>
            ))
          )}
        </div>
      ) : null}

      {isOverState(cell.state) ? (
        <div className="flex flex-col gap-2 rounded-md border bg-background p-3">
          <PanelLabel className="text-primary">Who could absorb +{need} h</PanelLabel>
          {candidates.map((candidate) => (
            <div key={candidate.resourceId} className="flex items-center gap-2.5">
              <div className="flex min-w-0 flex-1 flex-col gap-px">
                <span className="truncate text-sm font-semibold text-heading">
                  {candidate.name}
                </span>
                <span className="text-xs leading-snug text-muted-foreground">
                  {candidate.reasons.join(" · ")}
                </span>
              </div>
              <span className="shrink-0 rounded-sm bg-[var(--avail-available)] px-2 py-0.5 font-mono text-xs font-semibold text-[color:var(--avail-available-fg)]">
                {formatHours(candidate.freeHours)} h
              </span>
            </div>
          ))}
          <span className="text-xs text-heading">
            {candidates.length === 0
              ? `Nobody in ${scope} has room this month.`
              : covered >= need
                ? `Together they cover the ${need} h.`
                : `Together they cover ${covered} of ${need} h. Look outside ${scope}.`}
          </span>
          <span className="text-xs text-muted-foreground">
            Nothing changes here — assign on the Solution&apos;s Pod.
          </span>
        </div>
      ) : null}

      {isFreeState(cell.state) ? (
        <Note tone="info">
          {formatHours(cell.netHours)} h bookable in {fullMonthLabel(month).split(" ")[0]}.
        </Note>
      ) : null}

      <div className="flex flex-col gap-2">
        <PanelLabel>6-month view</PanelLabel>
        <div className="relative grid h-[84px] grid-cols-6 items-end gap-1.5">
          <span
            aria-hidden
            className="absolute inset-x-0 bottom-[50px] border-t border-dashed border-[color:var(--avail-unplanned-border)]"
          />
          {trend.map(({ month: trendMonth, cell: trendCell }) => (
            <div key={trendMonth} className="flex flex-col items-center gap-1">
              <span className="font-mono text-xs text-muted-foreground">
                {trendCell.pct !== null
                  ? `${Math.round(trendCell.pct)}%`
                  : trendCell.state === "unplanned"
                    ? "?"
                    : "–"}
              </span>
              <span
                title={`${monthLabel(trendMonth)}: ${STATE_LABEL[trendCell.state]}${
                  cellText(trendCell) ? ` (${cellText(trendCell)})` : ""
                }`}
                className={cn(
                  "w-full max-w-[26px] rounded-[2px]",
                  BAR_CLASS[trendCell.state],
                  trendMonth === month &&
                    "outline outline-2 outline-offset-2 outline-[color:var(--heading)]",
                )}
                style={{ height: Math.max(3, barRatio(trendCell) * 48) }}
              />
              <span
                className={cn(
                  "text-xs text-heading",
                  trendMonth === month ? "font-semibold" : "font-normal",
                )}
              >
                {monthLabel(trendMonth, { short: true })}
              </span>
            </div>
          ))}
        </div>
      </div>

      {canOpenResource ? (
        <div className="flex flex-wrap gap-2">
          <Link
            to="/dashboard/resources"
            search={{ q: row.name }}
            className={buttonVariants({ variant: "outline" })}
          >
            Open resource
          </Link>
        </div>
      ) : null}
    </PanelShell>
  );
}
